import type * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Badge from '@cloudscape-design/components/badge';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ButtonDropdown from '@cloudscape-design/components/button-dropdown';
import ContentLayout from '@cloudscape-design/components/content-layout';
import CopyToClipboard from '@cloudscape-design/components/copy-to-clipboard';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Table from '@cloudscape-design/components/table';
import { ApiTenantScope, type ApiKey, type CreateApiKeyResult } from '@etiquetachef/types';
import { subscribeApiKeys, type ApiKeyListResult } from '@/lib/firestoreApiKeys';
import {
  adminDeleteApiKeyCallable,
  adminRevokeApiKeyCallable,
  adminRotateApiKeySecretCallable,
  adminUpdateApiKeyCallable,
  wasReportedByCallable,
} from '@/lib/callables';
import { listCrmPartners } from '@/lib/firestoreCrmPartners';
import { reportNonFatal, serializeError } from '@/lib/errorReport';
import { toast } from '@/lib/notifications';
import { AdminConfirm } from '@/components/ui/AdminConfirm';
import { ApiKeyEditorDialog } from '@/features/api-keys/ApiKeyEditorDialog';
import { ApiKeyCreatedModal } from '@/features/api-keys/ApiKeyCreatedModal';

/**
 * Chave i18n do rótulo de um scope. Não pode conter `:` — o i18next trata
 * dois-pontos como separador de namespace, e `scopes.tenants:read` era
 * resolvido como namespace `…scopes.tenants` + chave `read`, fazendo a UI
 * exibir literalmente "read" em vez do nome do recurso.
 *
 * Origem: #263 — commit e02451674, 2026-08-24 17:36 +0200 (correção do
 * formulário de integração).
 *
 * Usado por: `ApiKeysPage` (coluna de escopos). Não voltar a usar o scope cru
 * (`tenants:read`) como chave i18n.
 */
function scopeLabelKey(scope: string): string {
  const [resource] = scope.split(':');
  return `admin.apiKeys.scopes.${resource ?? ''}Read`;
}

/** Ações que a barra da tabela (e o menu da linha) aplica a N integrações (#791). */
type BulkAction = 'pause' | 'resume' | 'revoke' | 'delete';

/**
 * Quem pode receber cada ação. É espelho da regra do servidor, para habilitar
 * o botão e contar as elegíveis; quem decide de verdade é o callable (o
 * `adminDeleteApiKey` recusa a não revogada, o `adminRevokeApiKey` recusa a
 * já revogada).
 */
const BULK_ELIGIBLE: Record<BulkAction, (k: ApiKey) => boolean> = {
  pause: (k) => k.active && !k.revokedAt,
  resume: (k) => !k.active && !k.revokedAt,
  revoke: (k) => !k.revokedAt,
  delete: (k) => !!k.revokedAt,
};

/** Toast de sucesso quando a ação atingiu uma integração só (texto já existente). */
const SINGLE_SUCCESS_KEY: Record<BulkAction, string> = {
  pause: 'admin.apiKeys.success.paused',
  resume: 'admin.apiKeys.success.resumed',
  revoke: 'admin.apiKeys.success.revoked',
  delete: 'admin.apiKeys.success.deleted',
};

/** Ordem dos botões na barra: reversíveis primeiro, definitivas no fim. */
const BULK_ORDER: readonly BulkAction[] = ['pause', 'resume', 'revoke', 'delete'];

/** Ação que pede `AdminConfirm` antes de rodar, com as integrações já filtradas. */
type PendingConfirm = {
  action: 'revoke' | 'delete';
  keys: ApiKey[];
  /** Selecionadas que não se aplicam à ação, avisadas no diálogo. */
  skipped: number;
  /** Veio da barra (mexe na seleção) ou do menu da linha (não mexe). */
  fromSelection: boolean;
};

/**
 * Chama o callable de uma ação para uma integração. Um callable por
 * integração, para cada uma ter o próprio audit e a própria recusa do
 * servidor (#791).
 */
function runOne(action: BulkAction, key: ApiKey): Promise<unknown> {
  switch (action) {
    case 'pause':
      return adminUpdateApiKeyCallable({ keyId: key.id, active: false });
    case 'resume':
      return adminUpdateApiKeyCallable({ keyId: key.id, active: true });
    case 'revoke':
      return adminRevokeApiKeyCallable({ keyId: key.id });
    case 'delete':
      return adminDeleteApiKeyCallable({ keyId: key.id });
  }
}

/**
 * Integrações da API pública readonly (issue #263) — `/settings/api-keys`.
 *
 * Cada linha é o alcance de um sistema de terceiro sobre os dados das
 * empresas. O `clientId` é público e copiável direto da tabela; o
 * `client_secret` NÃO é exibível — o banco guarda só o sha256, então não há o
 * que revelar. Quem perdeu o segredo usa "Gerar novo secret", que emite outro
 * e mostra uma vez (padrão de Stripe/GitHub/AWS).
 *
 * Contrato completo da API em `docs/screens/api-keys-admin.md`.
 *
 * Coluna "Clientes" (#785): mostra a regra de alcance (Todos / Todos exceto
 * parceiros / Clientes de <parceiro>). O nome do parceiro vem de
 * `crm_partners`; se a lista não carregar, cai no id — a tabela não pode
 * sumir por causa de um rótulo.
 *
 * Seleção múltipla (#791): checkboxes na tabela e uma barra com Pausar,
 * Reativar, Revogar e Remover. Cada ação opera só nas selecionadas
 * **elegíveis** (`BULK_ELIGIBLE`), e as demais são ignoradas com aviso no
 * diálogo. Remover apaga o doc (`adminDeleteApiKey`) e só existe para a
 * revogada; o histórico fica no `/audit`.
 *
 * Origem: #263 — commit 89ca9fd6e, 2026-08-24 15:39 +0200.
 *
 * Usado por: rota `/settings/api-keys` do `apps/admin` (`router.tsx`, menu
 * Sistema no `AdminLayout`), só
 * para papéis com `manageApiKeys`. Callables: `adminUpdateApiKey`,
 * `adminRevokeApiKey`, `adminRotateApiKeySecret`, `adminDeleteApiKey`.
 *
 * Histórico:
 *  - 2026-10-01 13:44 +0200 — (#791) seleção múltipla, ações em lote e
 *    remoção da integração revogada.
 *  - 2026-10-01 14:59 +0200 — (#791, auditoria) ação do menu da linha não
 *    limpa a seleção, falhas continuam selecionadas, menu bloqueado durante
 *    uma ação.
 *  - 2026-10-25 15:00 +0100 — (#1021) falha do "Gerar novo secret" sai num
 *    `Alert` dentro do confirm, que fica aberto (o toast ficava atrás do
 *    overlay, mesmo achado do CRM, #174), e vai para a telemetria.
 */
export function ApiKeysPage(): React.JSX.Element {
  const { t } = useTranslation();
  const [state, setState] = useState<ApiKeyListResult>({ kind: 'LOADING' });
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ApiKey | null>(null);
  const [confirming, setConfirming] = useState<PendingConfirm | null>(null);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());
  const [running, setRunning] = useState<BulkAction | null>(null);
  const [createdCredentials, setCreatedCredentials] = useState<CreateApiKeyResult | null>(null);
  const [rotating, setRotating] = useState<ApiKey | null>(null);
  const [rotateLoading, setRotateLoading] = useState(false);
  // #1021 (2026-10-25 15:00 +0100): falha do rotate, mostrada dentro do confirm.
  const [rotateError, setRotateError] = useState<string | null>(null);

  const [partnerNames, setPartnerNames] = useState<ReadonlyMap<string, string>>(new Map());

  useEffect(() => {
    setState({ kind: 'LOADING' });
    return subscribeApiKeys(setState);
  }, []);

  const apiKeys = useMemo(() => (state.kind === 'READY' ? state.apiKeys : []), [state]);

  // A seleção guarda ids e é re-derivada a cada snapshot: assim o botão conta
  // o estado ATUAL da integração (pausada por outra aba, por exemplo) e quem
  // foi apagada sai da seleção sozinha.
  const selected = useMemo(() => apiKeys.filter((k) => selectedIds.has(k.id)), [apiKeys, selectedIds]);

  useEffect(() => {
    let cancelled = false;
    listCrmPartners()
      .then((list) => {
        if (!cancelled) setPartnerNames(new Map(list.map((p) => [p.id, p.name])));
      })
      .catch((err: unknown) => {
        console.error('ApiKeysPage listCrmPartners failed', err);
        reportNonFatal('admin_api_keys_partners_load_failed', {
          message: err instanceof Error ? err.message : String(err),
        });
      });
    return (): void => {
      cancelled = true;
    };
  }, []);

  /**
   * Rótulo do alcance da integração: a regra (#785) e, quando houver, quantos
   * demos foram liberados à mão além dela (#785, 2026-10-01).
   */
  function tenantScopeLabel(key: ApiKey): string {
    const rule = tenantScopeRuleLabel(key);
    const demos = key.demoTenantIds?.length ?? 0;
    return demos === 0
      ? rule
      : `${rule} ${t('admin.apiKeys.fields.demoTenantsCount', { count: demos })}`;
  }

  /** Rótulo só da regra de clientes da integração (#785). */
  function tenantScopeRuleLabel(key: ApiKey): string {
    switch (key.tenantScope) {
      case ApiTenantScope.ALL:
        return t('admin.apiKeys.fields.tenantScopeAll');
      case ApiTenantScope.NON_PARTNER:
        return t('admin.apiKeys.fields.tenantScopeNonPartner');
      case ApiTenantScope.PARTNER: {
        const id = key.scopePartnerId ?? '';
        return t('admin.apiKeys.fields.tenantScopePartnerNamed', {
          name: partnerNames.get(id) ?? id,
        });
      }
    }
  }

  /**
   * Roda uma ação em N integrações, com um callable por integração e
   * `Promise.allSettled`, para a falha de uma não interromper as outras.
   * Cada rejeição é reportada (o admin não tem DevTools em produção).
   *
   * Seleção ao fim (auditoria #791, 2026-10-01 14:59 +0200): vinda da barra,
   * ficam selecionadas só as que falharam, para o operador tentar de novo
   * sem caçá-las na lista. Vinda do menu da linha, a seleção não é tocada.
   * A primeira versão limpava sempre, e pausar uma linha pelo ⋮ apagava a
   * seleção em lote que o operador estava montando.
   */
  async function runAction(
    action: BulkAction,
    keys: readonly ApiKey[],
    fromSelection: boolean,
  ): Promise<void> {
    if (keys.length === 0) return;
    setRunning(action);
    try {
      const results = await Promise.allSettled(keys.map((k) => runOne(action, k)));
      const failedIds = new Set<string>();
      const failed: PromiseRejectedResult[] = [];
      results.forEach((r, i) => {
        if (r.status !== 'rejected') return;
        failed.push(r);
        const key = keys[i];
        if (key) failedIds.add(key.id);
      });
      for (const failure of failed) {
        const err: unknown = failure.reason;
        console.error(`api key ${action} failed`, err);
        reportNonFatal('admin_api_keys_action_failed', {
          action,
          total: keys.length,
          code: (err as { code?: unknown } | null)?.code ?? null,
          message: err instanceof Error ? err.message : String(err),
        });
      }
      if (failed.length === 0) {
        toast.success(
          keys.length === 1
            ? t(SINGLE_SUCCESS_KEY[action])
            : t('admin.apiKeys.bulk.done', { count: keys.length }),
        );
      } else if (keys.length === 1) {
        toast.error(t('admin.apiKeys.errors.actionFailed'));
      } else {
        toast.error(
          t('admin.apiKeys.bulk.partialFailure', { failed: failed.length, total: keys.length }),
        );
      }
      if (fromSelection) setSelectedIds(failedIds);
      setConfirming(null);
    } finally {
      setRunning(null);
    }
  }

  /**
   * Ponto de entrada da barra e do menu da linha. Revogar e Remover são
   * definitivas e passam pelo `AdminConfirm`; Pausar e Reativar rodam direto,
   * como já era no menu.
   */
  function startAction(action: BulkAction, pool: readonly ApiKey[], fromSelection: boolean): void {
    const keys = pool.filter(BULK_ELIGIBLE[action]);
    if (keys.length === 0) return;
    if (action === 'revoke' || action === 'delete') {
      setConfirming({ action, keys, skipped: pool.length - keys.length, fromSelection });
    } else {
      void runAction(action, keys, fromSelection);
    }
  }

  /** Gera novo `client_secret` (`adminRotateApiKeySecret`) e o mostra uma única vez. */
  async function confirmRotate(): Promise<void> {
    if (!rotating) return;
    setRotateLoading(true);
    setRotateError(null);
    try {
      const credentials = await adminRotateApiKeySecretCallable({ keyId: rotating.id });
      toast.success(t('admin.apiKeys.rotate.success'));
      setRotating(null);
      // Mesmo modal da criação: é a única vez que o novo segredo existe.
      setCreatedCredentials(credentials);
    } catch (err) {
      console.error('rotate api key secret failed', err);
      // Auditoria 2026-10-25 15:00 +0100 (#1021): era só `console.error` + `toast.error` — o toast
      // ficava atrás do overlay do confirm, que não fecha no erro (#174), e o console não chega a nós.
      // 2ª auditoria (2026-10-25 15:52 +0100, #1021): falha da callable já sai como `callable_failed`
      // pelo wrapper; aqui só o que ele não vê (Zod do input, offline) — senão eram 2 eventos.
      if (!wasReportedByCallable(err)) {
        reportNonFatal('admin_api_keys_rotate_failed', serializeError(err));
      }
      setRotateError(t('admin.apiKeys.errors.actionFailed'));
    } finally {
      setRotateLoading(false);
    }
  }

  /** Badge de estado da integração: revogada, pausada ou ativa. */
  function statusBadge(key: ApiKey): React.ReactNode {
    if (key.revokedAt) return <Badge color="red">{t('admin.apiKeys.status.revoked')}</Badge>;
    if (!key.active) return <Badge color="grey">{t('admin.apiKeys.status.paused')}</Badge>;
    return <Badge color="green">{t('admin.apiKeys.status.active')}</Badge>;
  }

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description={t('admin.apiKeys.subtitle')}
          actions={
            <Button
              variant="primary"
              onClick={(): void => {
                setCreating(true);
              }}
            >
              {t('admin.apiKeys.create')}
            </Button>
          }
        >
          {t('admin.apiKeys.title')}
        </Header>
      }
    >
      <SpaceBetween size="m">
        {state.kind === 'ERROR' && <Alert type="error">{t('admin.apiKeys.errors.load')}</Alert>}

        <Table<ApiKey>
          items={apiKeys}
          loading={state.kind === 'LOADING'}
          trackBy="id"
          variant="container"
          resizableColumns
          stickyHeader
          selectionType="multi"
          selectedItems={selected}
          onSelectionChange={({ detail }): void => {
            setSelectedIds(new Set(detail.selectedItems.map((k) => k.id)));
          }}
          ariaLabels={{
            selectionGroupLabel: t('admin.apiKeys.bulk.selectAll'),
            itemSelectionLabel: (_data, row): string => t('admin.apiKeys.bulk.selectRow', { name: row.name }),
            allItemsSelectionLabel: (): string => t('admin.apiKeys.bulk.selectAll'),
          }}
          header={
            <Header
              variant="h2"
              counter={selected.length > 0 ? `(${String(selected.length)}/${String(apiKeys.length)})` : undefined}
              actions={
                <SpaceBetween size="xs" direction="horizontal">
                  {/* Atalho do pedido original (#791): limpar as revogadas sem
                      marcar uma a uma. */}
                  <Button
                    disabled={running !== null || !apiKeys.some(BULK_ELIGIBLE.delete)}
                    onClick={(): void => {
                      setSelectedIds(new Set(apiKeys.filter(BULK_ELIGIBLE.delete).map((k) => k.id)));
                    }}
                  >
                    {t('admin.apiKeys.bulk.selectRevoked')}
                  </Button>
                  {BULK_ORDER.map((action) => {
                    const eligible = selected.filter(BULK_ELIGIBLE[action]).length;
                    return (
                      <Button
                        key={action}
                        loading={running === action}
                        disabled={eligible === 0 || (running !== null && running !== action)}
                        onClick={(): void => {
                          startAction(action, selected, true);
                        }}
                      >
                        {t(`admin.apiKeys.bulk.${action}`, { count: eligible })}
                      </Button>
                    );
                  })}
                </SpaceBetween>
              }
            >
              {t('admin.apiKeys.bulk.tableTitle')}
            </Header>
          }
          empty={
            <Box textAlign="center" color="text-status-inactive" padding="m">
              {t('admin.apiKeys.empty')}
            </Box>
          }
          columnDefinitions={[
            {
              id: 'name',
              header: t('admin.apiKeys.columns.name'),
              cell: (k): React.ReactNode => k.name,
            },
            {
              id: 'clientId',
              header: t('admin.apiKeys.columns.clientId'),
              minWidth: 260,
              cell: (k): React.ReactNode => (
                <CopyToClipboard
                  variant="inline"
                  textToCopy={k.clientId}
                  copyButtonAriaLabel={t('admin.apiKeys.actions.copyClientId')}
                  copySuccessText={t('admin.apiKeys.copied')}
                  copyErrorText={t('admin.apiKeys.copyFailed')}
                />
              ),
            },
            {
              id: 'clientSecret',
              header: t('admin.apiKeys.columns.clientSecret'),
              // Não há o que copiar: o banco guarda só o sha256. Mostrar a
              // ausência explicitamente evita o operador procurar o valor.
              cell: (): React.ReactNode => (
                <Box color="text-status-inactive" fontSize="body-s">
                  {`•••••••• (${t('admin.apiKeys.secretHidden')})`}
                </Box>
              ),
            },
            {
              id: 'tenants',
              header: t('admin.apiKeys.columns.tenants'),
              cell: (k): React.ReactNode => tenantScopeLabel(k),
            },
            {
              id: 'scopes',
              header: t('admin.apiKeys.columns.scopes'),
              cell: (k): React.ReactNode => (
                <SpaceBetween direction="horizontal" size="xxs">
                  {k.scopes.map((scope) => (
                    <Badge key={scope}>{t(scopeLabelKey(scope))}</Badge>
                  ))}
                </SpaceBetween>
              ),
            },
            {
              id: 'rateLimit',
              header: t('admin.apiKeys.columns.rateLimit'),
              cell: (k): React.ReactNode => t('admin.apiKeys.perMinute', { count: k.requestsPerMinute }),
            },
            {
              id: 'allowedIps',
              header: t('admin.apiKeys.columns.allowedIps'),
              cell: (k): React.ReactNode =>
                k.allowedIps.length === 0
                  ? t('admin.apiKeys.anyIp')
                  : t('admin.apiKeys.ipCount', { count: k.allowedIps.length }),
            },
            {
              id: 'portalEmails',
              header: t('admin.apiKeys.columns.portalEmails'),
              // Quantas contas do parceiro enxergam a documentação (#265).
              // Vazio é o default e significa doc fechada — não "sem restrição",
              // ao contrário da coluna de IPs ao lado.
              cell: (k): React.ReactNode =>
                (k.portalEmails?.length ?? 0) === 0
                  ? t('admin.apiKeys.noPortalAccess')
                  : t('admin.apiKeys.portalEmailCount', { count: k.portalEmails?.length ?? 0 }),
            },
            {
              id: 'status',
              header: t('admin.apiKeys.columns.status'),
              cell: statusBadge,
            },
            {
              id: 'lastUsed',
              header: t('admin.apiKeys.columns.lastUsed'),
              cell: (k): React.ReactNode =>
                k.lastUsedAtMillis ? new Date(k.lastUsedAtMillis).toLocaleString() : '—',
            },
            {
              id: 'actions',
              header: t('admin.apiKeys.columns.actions'),
              minWidth: 90,
              cell: (k): React.ReactNode => (
                <ButtonDropdown
                  variant="icon"
                  // Bloqueado enquanto uma ação roda: um segundo `runAction`
                  // em paralelo sobrescreveria `running`, e o primeiro a acabar
                  // reabilitaria a barra com o outro ainda em voo (#791).
                  disabled={running !== null}
                  ariaLabel={t('admin.apiKeys.actions.menu')}
                  expandToViewport
                  items={[
                    // Integração revogada é histórico: nada mais se faz nela.
                    { id: 'edit', text: t('admin.apiKeys.actions.edit'), disabled: !!k.revokedAt },
                    {
                      id: 'pause',
                      text: t(k.active ? 'admin.apiKeys.actions.pause' : 'admin.apiKeys.actions.resume'),
                      disabled: !!k.revokedAt,
                    },
                    {
                      id: 'rotate',
                      text: t('admin.apiKeys.actions.rotateSecret'),
                      disabled: !!k.revokedAt,
                    },
                    {
                      id: 'revoke',
                      text: t('admin.apiKeys.actions.revoke'),
                      disabled: !!k.revokedAt,
                    },
                    // #791 — a revogada só pode sair da lista.
                    {
                      id: 'delete',
                      text: t('admin.apiKeys.actions.delete'),
                      disabled: !k.revokedAt,
                    },
                  ]}
                  onItemClick={({ detail }): void => {
                    if (detail.id === 'edit') setEditing(k);
                    else if (detail.id === 'pause') startAction(k.active ? 'pause' : 'resume', [k], false);
                    else if (detail.id === 'rotate') {
                      setRotateError(null);
                      setRotating(k);
                    }
                    else if (detail.id === 'revoke') startAction('revoke', [k], false);
                    else if (detail.id === 'delete') startAction('delete', [k], false);
                  }}
                />
              ),
            },
          ]}
        />
      </SpaceBetween>

      <ApiKeyEditorDialog
        open={creating || editing !== null}
        mode={editing ? 'edit' : 'create'}
        apiKey={editing}
        onClose={(): void => {
          setCreating(false);
          setEditing(null);
        }}
        onCreated={setCreatedCredentials}
      />

      <ApiKeyCreatedModal
        credentials={createdCredentials}
        onDismiss={(): void => {
          setCreatedCredentials(null);
        }}
      />

      <AdminConfirm
        visible={rotating !== null}
        title={t('admin.apiKeys.rotate.title')}
        confirmLabel={t('admin.apiKeys.rotate.confirm')}
        loading={rotateLoading}
        onConfirm={(): void => {
          void confirmRotate();
        }}
        onDismiss={(): void => {
          setRotating(null);
        }}
      >
        <SpaceBetween size="s">
          <Alert type="warning">{t('admin.apiKeys.rotate.warning')}</Alert>
          <Box>{t('admin.apiKeys.rotate.body', { name: rotating?.name ?? '' })}</Box>
          {/* Auditoria 2026-10-25 15:00 +0100 (#1021): erro dentro do confirm, junto do footer (#174). */}
          {rotateError !== null && <Alert type="error">{rotateError}</Alert>}
        </SpaceBetween>
      </AdminConfirm>

      <AdminConfirm
        visible={confirming !== null}
        title={t(`admin.apiKeys.${confirming?.action ?? 'revoke'}.title`)}
        confirmLabel={t(`admin.apiKeys.${confirming?.action ?? 'revoke'}.confirm`)}
        loading={running !== null}
        onConfirm={(): void => {
          if (confirming) void runAction(confirming.action, confirming.keys, confirming.fromSelection);
        }}
        onDismiss={(): void => {
          setConfirming(null);
        }}
      >
        {confirming && (
          <SpaceBetween size="s">
            <Alert type="warning">{t(`admin.apiKeys.${confirming.action}.warning`)}</Alert>
            <Box>
              {confirming.keys.length === 1
                ? t(`admin.apiKeys.${confirming.action}.body`, { name: confirming.keys[0]?.name ?? '' })
                : t(`admin.apiKeys.bulk.${confirming.action}Body`, { count: confirming.keys.length })}
            </Box>
            {confirming.skipped > 0 && (
              <Box color="text-status-inactive">
                {t('admin.apiKeys.bulk.skipped', { count: confirming.skipped })}
              </Box>
            )}
          </SpaceBetween>
        )}
      </AdminConfirm>
    </ContentLayout>
  );
}
